/**
 * OAuth Connector configurations, types, and utility functions.
 * Handles OAuth 2.0 authorization flows for all supported third-party integrations.
 * Works alongside mcpAuth.ts (Cognito) — this handles OAuth redirect flows.
 */

// ─── Types ──────────────────────────────────────────────────────────────

export type OAuthPlatform =
  | 'jira'
  | 'confluence'
  | 'notion'
  | 'slack'
  | 'github'
  | 'google'
  | 'teams'

export type OAuthEnvironment = 'local' | 'staging' | 'production'

export type TokenScope = 'per-user' | 'per-tenant'

export interface OAuthConnectorConfig {
  id: OAuthPlatform
  name: string
  authUrl: string
  tokenUrl: string
  defaultScopes: string[]
  scopeDescriptions: Record<string, string>
  brandColor: string
  responseType: 'code'
  grantType: 'authorization_code'
  extraParams?: Record<string, string>
}

export interface OAuthCredentials {
  clientId: string
  clientSecret: string
  scopes: string[]
  configuredAt: string
}

export interface OAuthState {
  tenant_id: string
  user_id: string
  platform: OAuthPlatform
  csrf_token: string
  environment: OAuthEnvironment
  redirect_uri: string
}

export interface OAuthExchangeRequest {
  platform: OAuthPlatform
  code: string
  tenant_id: string
  user_id: string
  redirect_uri: string
}

export interface OAuthExchangeResponse {
  success: boolean
  scopes_granted: string[]
  expires_at: string
  error?: string
}

export interface ConnectorSetupState {
  environment: OAuthEnvironment
  redirectBase: string
  tenantId: string
  tokenScope: TokenScope
  selectedConnectors: OAuthPlatform[]
  connectorScopes: Record<string, string[]>
  credentials: Record<string, OAuthCredentials>
}

// ─── Environment Config ─────────────────────────────────────────────────

export const ENVIRONMENT_URLS: Record<OAuthEnvironment, string> = {
  local: 'http://localhost:3000',
  staging: 'https://staging.workium.app',
  production: 'https://app.workium.com',
}

// ─── Connector Configurations ───────────────────────────────────────────

export const OAUTH_CONNECTORS: OAuthConnectorConfig[] = [
  {
    id: 'jira',
    name: 'Jira',
    authUrl: 'https://auth.atlassian.com/authorize',
    tokenUrl: 'https://auth.atlassian.com/oauth/token',
    defaultScopes: ['read:jira-work', 'write:jira-work', 'offline_access'],
    scopeDescriptions: {
      'read:jira-work': 'Leer issues, sprints, boards y proyectos',
      'write:jira-work': 'Crear y editar issues, transiciones y comentarios',
      offline_access: 'Mantener acceso con refresh token (requerido)',
      'read:jira-user': 'Leer perfiles de usuarios de Jira',
      'manage:jira-project': 'Administrar configuración de proyectos',
    },
    brandColor: '#2684FF',
    responseType: 'code',
    grantType: 'authorization_code',
    extraParams: {
      audience: 'api.atlassian.com',
      prompt: 'consent',
    },
  },
  {
    id: 'confluence',
    name: 'Confluence',
    authUrl: 'https://auth.atlassian.com/authorize',
    tokenUrl: 'https://auth.atlassian.com/oauth/token',
    defaultScopes: [
      'read:confluence-content.all',
      'write:confluence-content',
      'offline_access',
    ],
    scopeDescriptions: {
      'read:confluence-content.all': 'Leer todas las páginas y espacios',
      'write:confluence-content': 'Crear y editar páginas',
      offline_access: 'Mantener acceso con refresh token (requerido)',
      'read:confluence-space.summary': 'Leer resúmenes de espacios',
      'search:confluence': 'Buscar contenido en Confluence',
    },
    brandColor: '#1868DB',
    responseType: 'code',
    grantType: 'authorization_code',
    extraParams: {
      audience: 'api.atlassian.com',
      prompt: 'consent',
    },
  },
  {
    id: 'notion',
    name: 'Notion',
    authUrl: 'https://api.notion.com/v1/oauth/authorize',
    tokenUrl: 'https://api.notion.com/v1/oauth/token',
    defaultScopes: [],
    scopeDescriptions: {
      read_content: 'Notion usa permisos granulares por página (owner=user)',
    },
    brandColor: '#000000',
    responseType: 'code',
    grantType: 'authorization_code',
    extraParams: {
      owner: 'user',
    },
  },
  {
    id: 'slack',
    name: 'Slack',
    authUrl: 'https://slack.com/oauth/v2/authorize',
    tokenUrl: 'https://slack.com/api/oauth.v2.access',
    defaultScopes: ['channels:read', 'chat:write', 'files:read', 'users:read'],
    scopeDescriptions: {
      'channels:read': 'Listar canales y su información',
      'chat:write': 'Enviar mensajes a canales',
      'files:read': 'Leer archivos compartidos',
      'users:read': 'Leer perfiles de usuarios',
      'reactions:read': 'Leer reacciones en mensajes',
      'groups:read': 'Leer canales privados',
      'im:read': 'Leer mensajes directos',
    },
    brandColor: '#4A154B',
    responseType: 'code',
    grantType: 'authorization_code',
    extraParams: {
      user_scope: 'channels:read,chat:write,files:read,users:read',
    },
  },
  {
    id: 'github',
    name: 'GitHub',
    authUrl: 'https://github.com/login/oauth/authorize',
    tokenUrl: 'https://github.com/login/oauth/access_token',
    defaultScopes: ['repo', 'read:org', 'read:user'],
    scopeDescriptions: {
      repo: 'Acceso completo a repositorios (código, PRs, issues)',
      'read:org': 'Leer membresía y equipos de la organización',
      'read:user': 'Leer perfil del usuario',
      workflow: 'Gestionar GitHub Actions workflows',
      'admin:repo_hook': 'Administrar webhooks de repositorios',
      'read:packages': 'Leer paquetes de GitHub Packages',
    },
    brandColor: '#FFFFFF',
    responseType: 'code',
    grantType: 'authorization_code',
  },
  {
    id: 'google',
    name: 'Google Workspace',
    authUrl: 'https://accounts.google.com/o/oauth2/v2/auth',
    tokenUrl: 'https://oauth2.googleapis.com/token',
    defaultScopes: [
      'https://www.googleapis.com/auth/drive.readonly',
      'https://www.googleapis.com/auth/calendar.readonly',
      'https://www.googleapis.com/auth/gmail.readonly',
    ],
    scopeDescriptions: {
      'https://www.googleapis.com/auth/drive.readonly':
        'Leer archivos de Google Drive',
      'https://www.googleapis.com/auth/calendar.readonly':
        'Leer eventos del calendario',
      'https://www.googleapis.com/auth/gmail.readonly': 'Leer correos de Gmail',
      'https://www.googleapis.com/auth/documents.readonly': 'Leer Google Docs',
      'https://www.googleapis.com/auth/spreadsheets.readonly':
        'Leer Google Sheets',
      'https://www.googleapis.com/auth/presentations.readonly':
        'Leer Google Slides',
    },
    brandColor: '#4285F4',
    responseType: 'code',
    grantType: 'authorization_code',
    extraParams: {
      access_type: 'offline',
      prompt: 'consent',
    },
  },
  {
    id: 'teams',
    name: 'Microsoft Teams',
    authUrl: 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize',
    tokenUrl: 'https://login.microsoftonline.com/common/oauth2/v2.0/token',
    defaultScopes: [
      'User.Read',
      'Chat.ReadWrite',
      'ChannelMessage.Send',
      'offline_access',
    ],
    scopeDescriptions: {
      'User.Read': 'Leer tu perfil de Microsoft',
      'Chat.ReadWrite': 'Leer y enviar mensajes en chats',
      'ChannelMessage.Send': 'Enviar mensajes en canales de Teams',
      'Team.ReadBasic.All': 'Leer equipos y canales',
      'Channel.ReadBasic.All': 'Leer información básica de canales',
      offline_access: 'Mantener acceso con refresh token (requerido)',
    },
    brandColor: '#6264A7',
    responseType: 'code',
    grantType: 'authorization_code',
    extraParams: {
      response_mode: 'query',
    },
  },
]

// ─── Storage ────────────────────────────────────────────────────────────

const STORAGE_KEY = 'vsm_oauth_setup'

export function loadSetupState(): ConnectorSetupState | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) return JSON.parse(stored)
  } catch (e) {
    console.error('[OAuthConnectors] Failed to load setup state', e)
  }
  return null
}

export function saveSetupState(state: ConnectorSetupState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch (e) {
    console.error('[OAuthConnectors] Failed to save setup state', e)
  }
}

export function getCredentials(
  platform: OAuthPlatform,
): OAuthCredentials | null {
  const state = loadSetupState()
  return state?.credentials[platform] ?? null
}

// ─── OAuth Flow Utilities ───────────────────────────────────────────────

function generateCsrfToken(): string {
  const array = new Uint8Array(32)
  crypto.getRandomValues(array)
  return Array.from(array, (b) => b.toString(16).padStart(2, '0')).join('')
}

export function encodeOAuthState(state: OAuthState): string {
  return btoa(JSON.stringify(state))
}

export function decodeOAuthState(encoded: string): OAuthState | null {
  try {
    return JSON.parse(atob(encoded))
  } catch {
    return null
  }
}

/**
 * Initiate OAuth authorization redirect for a given platform.
 * Constructs the full authorization URL with state parameter and redirects.
 */
export function initiateOAuth(
  platform: OAuthPlatform,
  tenantId: string,
  userId: string,
  environment: OAuthEnvironment = 'production',
): void {
  const connector = OAUTH_CONNECTORS.find((c) => c.id === platform)
  if (!connector) throw new Error(`Unknown platform: ${platform}`)

  const credentials = getCredentials(platform)
  if (!credentials) throw new Error(`No credentials configured for ${platform}`)

  const baseUrl = ENVIRONMENT_URLS[environment]
  const redirectUri = `${baseUrl}/integrations/callback/${platform}`

  const csrfToken = generateCsrfToken()
  // Store CSRF for validation on callback
  sessionStorage.setItem(`oauth_csrf_${platform}`, csrfToken)

  const state = encodeOAuthState({
    tenant_id: tenantId,
    user_id: userId,
    platform,
    csrf_token: csrfToken,
    environment,
    redirect_uri: redirectUri,
  })

  const params = new URLSearchParams({
    client_id: credentials.clientId,
    response_type: connector.responseType,
    redirect_uri: redirectUri,
    scope: credentials.scopes.join(' '),
    state,
    ...(connector.extraParams || {}),
  })

  const authUrl = `${connector.authUrl}?${params.toString()}`
  console.log(`[OAuth] Redirecting to ${platform} authorization...`)
  window.location.href = authUrl
}

/**
 * Exchange authorization code for tokens (calls backend).
 */
export async function exchangeOAuthCode(
  request: OAuthExchangeRequest,
): Promise<OAuthExchangeResponse> {
  try {
    const response = await fetch('/api/integrations/exchange', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    })

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ error: 'Exchange failed' }))
      return {
        success: false,
        scopes_granted: [],
        expires_at: '',
        error: error.error,
      }
    }

    return await response.json()
  } catch (err) {
    return {
      success: false,
      scopes_granted: [],
      expires_at: '',
      error: err instanceof Error ? err.message : 'Network error',
    }
  }
}

/**
 * Validate CSRF token from OAuth callback state.
 */
export function validateCsrfToken(
  platform: OAuthPlatform,
  csrfToken: string,
): boolean {
  const stored = sessionStorage.getItem(`oauth_csrf_${platform}`)
  if (!stored) return false
  sessionStorage.removeItem(`oauth_csrf_${platform}`)
  return stored === csrfToken
}

// ─── Helpers ────────────────────────────────────────────────────────────

export function getConnectorConfig(
  platform: OAuthPlatform,
): OAuthConnectorConfig | undefined {
  return OAUTH_CONNECTORS.find((c) => c.id === platform)
}

export function getShortScopeLabel(scope: string): string {
  // Shorten Google scopes for display
  if (scope.startsWith('https://www.googleapis.com/auth/')) {
    return scope.replace('https://www.googleapis.com/auth/', '')
  }
  return scope
}

export function isConnectorConfigured(platform: OAuthPlatform): boolean {
  const creds = getCredentials(platform)
  return !!creds?.clientId
}
